import type { AuthConfig, CommitmentsData, MethodologyDoc, SectorDefinition } from './types';
import { dataService } from './services/DataService';
import { store } from './core/store';
import { router } from './core/router';
import { applyDocumentChrome } from './core/theme';
import { chrome } from './i18n';
import { NationalView } from './views/NationalView';
import { CommitmentsView } from './views/CommitmentsView';
import { ProgramView } from './views/ProgramView';
import { MethodologyView, flattenMethodology, renderMethodologyResults } from './views/MethodologyView';
import { LoginView } from './views/LoginView';
import { authRequired, isAuthed, verify, grant, logout } from './core/auth';
import { SectorView } from './views/SectorView';
import { DomainView } from './views/DomainView';
import { RecModal } from './components/RecModal';
import { buildChart } from './charts/chartFactory';
import { applySegments, type SegmentSelection } from './core/segments';
import type { Chart } from 'chart.js';

interface Route {
  level: 'national' | 'commitments' | 'program' | 'methodology' | 'sector' | 'domain';
  sector?: SectorDefinition;
  domainId?: string;
  programId?: string;
}

const NATIONAL_ACCENT = '#3b82f6';

export class App {
  private sectors: SectorDefinition[] = [];
  private commitments?: CommitmentsData;
  private methodology?: MethodologyDoc;
  private methodologyQuery = '';
  private auth?: AuthConfig;
  private charts: Chart[] = [];
  /** Active period/sub-sector filter; persists within a sector, resets on sector change. */
  private filter: SegmentSelection = { stage: 0, year: 0 };
  private lastSector = '';

  constructor(private readonly root: HTMLElement) {}

  async init(): Promise<void> {
    this.renderLoading();
    try {
      this.auth = await dataService.getAuth();
    } catch {
      this.auth = undefined;
    }
    // Re-render the login screen on language change before sign-in.
    store.subscribe(() => {
      if (authRequired(this.auth) && !isAuthed()) this.renderLogin();
    });
    if (authRequired(this.auth) && !isAuthed()) {
      this.renderLogin();
      return;
    }
    await this.boot();
  }

  private renderLogin(error = ''): void {
    const { locale, theme } = store.get();
    document.documentElement.setAttribute('lang', locale);
    document.documentElement.setAttribute('dir', locale === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('data-theme', theme);
    document.title = chrome(locale).loginTitle;
    this.root.setAttribute('aria-busy', 'false');
    this.root.innerHTML = LoginView(locale, theme, error);

    this.root.querySelectorAll<HTMLButtonElement>('[data-lang]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const lang = btn.dataset.lang;
        if (lang === 'ar' || lang === 'en') store.setLocale(lang);
      });
    });
    const form = this.root.querySelector<HTMLFormElement>('#loginForm');
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!this.auth) return;
      const user = (this.root.querySelector('#loginUser') as HTMLInputElement)?.value ?? '';
      const pass = (this.root.querySelector('#loginPass') as HTMLInputElement)?.value ?? '';
      const btn = this.root.querySelector<HTMLButtonElement>('#loginBtn');
      if (btn) btn.disabled = true;
      const match = await verify(this.auth, user, pass);
      if (match) {
        grant(match);
        await this.boot();
      } else {
        this.renderLogin(chrome(store.get().locale).loginError);
      }
    });
  }

  private async boot(): Promise<void> {
    this.renderLoading();
    try {
      this.sectors = await dataService.getSectors();
    } catch (err) {
      console.error(err);
      this.renderError();
      return;
    }
    if (this.sectors.length === 0) {
      this.renderError();
      return;
    }
    try {
      this.commitments = await dataService.getCommitments();
    } catch (err) {
      console.warn('Commitments data unavailable:', err);
    }
    try {
      this.methodology = await dataService.getMethodology();
    } catch (err) {
      console.warn('Methodology data unavailable:', err);
    }

    store.setPath(this.normalize(router.current() || 'national'));
    router.onChange((p) => store.setPath(this.normalize(p || 'national')));
    store.subscribe(() => this.render());
    this.render();
  }

  /** Validates a raw hash path against known sectors/domains. */
  private normalize(raw: string): string {
    const [a, b] = raw.split('/').filter(Boolean);
    if (!a || a === 'national') return 'national';
    if (a === 'commitments') {
      if (b && this.commitments?.programs.some((p) => p.id === b)) return `commitments/${b}`;
      return 'commitments';
    }
    if (a === 'methodology') return 'methodology';
    const sector = this.sectors.find((s) => s.id === a);
    if (!sector) return 'national';
    if (b && sector.content.en.domains.some((d) => d.id === b)) return `${a}/${b}`;
    return a;
  }

  private parse(path: string): Route {
    const [a, b] = path.split('/').filter(Boolean);
    if (!a || a === 'national') return { level: 'national' };
    if (a === 'commitments') return b ? { level: 'program', programId: b } : { level: 'commitments' };
    if (a === 'methodology') return { level: 'methodology' };
    const sector = this.sectors.find((s) => s.id === a) ?? this.sectors[0];
    if (b) return { level: 'domain', sector, domainId: b };
    return { level: 'sector', sector };
  }

  private render(): void {
    const { locale, theme, path } = store.get();
    const route = this.parse(path);
    this.charts.forEach((c) => c.destroy());
    this.charts = [];
    this.root.setAttribute('aria-busy', 'false');
    router.navigate(path);

    if (route.level === 'national') {
      this.lastSector = '';
      applyDocumentChrome(locale, theme, NATIONAL_ACCENT);
      document.title = chrome(locale).nationalTitle;
      this.root.innerHTML = NationalView({ sectors: this.sectors, commitments: this.commitments, locale, theme });
      this.bindControls();
      return;
    }

    if ((route.level === 'commitments' || route.level === 'program') && !this.commitments) {
      store.setPath('national');
      return;
    }
    if (route.level === 'methodology' && !this.methodology) {
      store.setPath('national');
      return;
    }

    if (route.level === 'commitments' && this.commitments) {
      this.lastSector = '';
      applyDocumentChrome(locale, theme, NATIONAL_ACCENT);
      document.title = chrome(locale).commitmentsTitle;
      this.root.innerHTML = CommitmentsView({ sectors: this.sectors, commitments: this.commitments, locale, theme });
      this.bindControls();
      return;
    }

    if (route.level === 'methodology' && this.methodology) {
      this.lastSector = '';
      applyDocumentChrome(locale, theme, NATIONAL_ACCENT);
      document.title = chrome(locale).methodologyTitle;
      this.root.innerHTML = MethodologyView({
        sectors: this.sectors,
        doc: this.methodology,
        locale,
        theme,
        query: this.methodologyQuery,
        hasCommitments: Boolean(this.commitments),
      });
      this.bindControls();
      this.bindMethodologySearch();
      return;
    }

    if (route.level === 'program' && this.commitments) {
      this.lastSector = '';
      const program =
        this.commitments.programs.find((p) => p.id === route.programId) ?? this.commitments.programs[0];
      applyDocumentChrome(locale, theme, program.accent);
      document.title = program.content[locale].name;
      this.root.innerHTML = ProgramView({ sectors: this.sectors, commitments: this.commitments, program, locale, theme });
      this.bindControls();
      return;
    }

    const base = route.sector!;
    if (base.id !== this.lastSector) {
      this.filter = { stage: 0, year: 0 };
      this.lastSector = base.id;
    }
    const active = applySegments(base, this.filter);
    applyDocumentChrome(locale, theme, active.accent);
    document.title = active.content[locale].title;

    if (route.level === 'sector') {
      this.root.innerHTML = SectorView({ sectors: this.sectors, active, locale, theme, selection: this.filter, hasCommitments: Boolean(this.commitments) });
      this.bindControls();
      this.bindFilters();
      this.bindRecommendations(active);
      return;
    }

    // domain
    const domain =
      active.content[locale].domains.find((d) => d.id === route.domainId) ??
      active.content[locale].domains[0];
    this.root.innerHTML = DomainView({ sectors: this.sectors, active, domain, locale, theme, selection: this.filter, hasCommitments: Boolean(this.commitments) });
    this.bindControls();
    this.bindFilters();
    this.bindRecommendations(active);
    this.renderDomainCharts(active, domain.id);
  }

  private bindFilters(): void {
    const ys = this.root.querySelector<HTMLSelectElement>('#yearSel');
    ys?.addEventListener('change', () => {
      this.filter = { ...this.filter, year: Number(ys.value) };
      this.render();
    });
    const ss = this.root.querySelector<HTMLSelectElement>('#stageSel');
    ss?.addEventListener('change', () => {
      this.filter = { ...this.filter, stage: Number(ss.value) };
      this.render();
    });
  }

  private bindMethodologySearch(): void {
    if (!this.methodology) return;
    const input = this.root.querySelector<HTMLInputElement>('#methSearch');
    const results = this.root.querySelector<HTMLElement>('#meth-results');
    if (!input || !results) return;
    const entries = flattenMethodology(this.methodology, this.sectors, store.get().locale);
    input.addEventListener('input', () => {
      this.methodologyQuery = input.value;
      results.innerHTML = renderMethodologyResults(entries, store.get().locale, input.value);
    });
  }

  private bindControls(): void {
    this.root.querySelectorAll<HTMLButtonElement>('[data-lang]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const lang = btn.dataset.lang;
        if (lang === 'ar' || lang === 'en') store.setLocale(lang);
      });
    });
    this.root.querySelector<HTMLButtonElement>('#themeBtn')?.addEventListener('click', () => {
      store.setTheme(store.get().theme === 'dark' ? 'light' : 'dark');
    });
    this.root.querySelector<HTMLButtonElement>('#printBtn')?.addEventListener('click', () => window.print());
    this.root.querySelector<HTMLButtonElement>('#logoutBtn')?.addEventListener('click', () => logout());
  }

  private bindRecommendations(active: SectorDefinition): void {
    const open = (ref: string) => this.openRecommendations(active, ref);
    this.root.querySelectorAll<HTMLElement>('[data-rec]').forEach((el) => {
      el.addEventListener('click', () => open(el.dataset.rec ?? ''));
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open(el.dataset.rec ?? '');
        }
      });
    });
  }

  private openRecommendations(active: SectorDefinition, ref: string): void {
    const [kind, id] = ref.split(':');
    const { locale } = store.get();
    const content = active.content[locale];
    const c = chrome(locale);
    let title = '';
    let meta: string | undefined;
    let items: string[] = [];
    let description: string | undefined;
    let directionLabel: string | undefined;
    let directionClass: 'up' | 'down' | 'neutral' | undefined;
    let directionIcon: string | undefined;

    if (kind === 'kpi') {
      const k = content.kpis.find((x) => x.id === id);
      if (!k?.recommendations?.length) return;
      title = k.label;
      meta = `${k.value} ${k.unit} (${k.delta})${k.target ? ` · ${c.targetLabel}: ${k.target.label}` : ''}`;
      items = k.recommendations;
      description = k.desc;
      if (k.direction) {
        directionClass = k.direction;
        directionLabel =
          k.direction === 'up' ? c.betterHigh : k.direction === 'down' ? c.betterLow : c.betterNeutral;
        directionIcon = k.direction === 'up' ? 'trending-up' : k.direction === 'down' ? 'trending-down' : 'minus';
      }
    } else if (kind === 'chart') {
      const ch = content.charts.find((x) => x.id === id);
      if (!ch?.recommendations?.length) return;
      title = ch.title;
      meta = ch.subtitle;
      items = ch.recommendations;
    } else if (kind === 'topic') {
      const topic =
        id === 'quality' ? content.quality : id === 'regions' ? content.regions : content.alerts;
      if (!topic.recommendations?.length) return;
      title = topic.title;
      meta = topic.subtitle;
      items = topic.recommendations;
    } else {
      return;
    }

    const mount = this.root.querySelector<HTMLElement>('#modal-root');
    if (!mount) return;
    mount.innerHTML = RecModal({ title, meta, items, recTitle: c.recTitle, closeLabel: c.close, description, whatLabel: c.whatItMeans, directionLabel, directionClass, directionIcon });
    const close = () => {
      mount.innerHTML = '';
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    mount.querySelectorAll<HTMLElement>('[data-close]').forEach((el) => {
      el.addEventListener('click', (e) => {
        if (e.target === el || el.classList.contains('modal-close')) close();
      });
    });
    mount.querySelector<HTMLElement>('.modal')?.addEventListener('click', (e) => e.stopPropagation());
    mount.querySelector<HTMLButtonElement>('.modal-close')?.focus();
  }

  private renderDomainCharts(active: SectorDefinition, domainId: string): void {
    const { locale } = store.get();
    const content = active.content[locale];
    const domain = content.domains.find((d) => d.id === domainId);
    if (!domain) return;
    for (const spec of content.charts.filter((ch) => domain.charts.includes(ch.id))) {
      const series = active.series[spec.seriesRef];
      if (!series) {
        console.warn(`Missing series "${spec.seriesRef}" for chart "${spec.id}"`);
        continue;
      }
      this.charts.push(buildChart(spec, series, locale));
    }
  }

  private renderLoading(): void {
    const c = chrome(store.get().locale);
    this.root.innerHTML = `<div class="state"><div class="spinner"></div><p>${c.loading}</p></div>`;
  }
  private renderError(): void {
    const c = chrome(store.get().locale);
    this.root.innerHTML = `<div class="state"><h2>${c.errorTitle}</h2><p>${c.errorBody}</p></div>`;
  }
}
